import { useState } from 'react'
import { Upload, Cloud, HardDrive, Box, Loader2 } from 'lucide-react'
import api from '../../core/services/api'

interface Provider {
  provider: string
  name: string
  connected: boolean
  icon: string
}

interface Props {
  allegatoId: string
  filename: string
  onToast: (msg: string, type: 'success' | 'error') => void
}

const ICONS: Record<string, typeof Cloud> = {
  cloud: Cloud,
  'hard-drive': HardDrive,
  box: Box,
}

export default function CloudUploadButton({ allegatoId, filename, onToast }: Props) {
  const [providers, setProviders] = useState<Provider[]>([])
  const [showMenu, setShowMenu] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const loadProviders = async () => {
    if (loaded) {
      setShowMenu(!showMenu)
      return
    }
    try {
      const resp = await api.get('/v1/cloud/providers')
      const connected = resp.data.filter((p: Provider) => p.connected)
      setProviders(connected)
      setLoaded(true)
      setShowMenu(true)
    } catch {
      onToast('Errore caricamento provider', 'error')
    }
  }

  const handleUpload = async (provider: string) => {
    setUploading(true)
    setShowMenu(false)
    try {
      await api.post(`/v1/cloud/${provider}/upload`, {
        allegato_id: allegatoId,
      })
      onToast(`"${filename}" caricato su ${provider}`, 'success')
    } catch (err: any) {
      onToast(err.response?.data?.detail || 'Errore upload', 'error')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={loadProviders}
        disabled={uploading}
        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors rounded"
        title="Carica su cloud"
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Upload className="w-4 h-4" />
        )}
      </button>

      {showMenu && providers.length > 0 && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[160px]">
          {providers.map(p => {
            const Icon = ICONS[p.icon] || Cloud
            return (
              <button
                key={p.provider}
                onClick={() => handleUpload(p.provider)}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 text-left"
              >
                <Icon className="w-4 h-4 text-gray-500" />
                <span>{p.name}</span>
              </button>
            )
          })}
        </div>
      )}

      {showMenu && providers.length === 0 && loaded && (
        <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 p-3 text-xs text-gray-500 min-w-[180px]">
          Nessun provider collegato. Vai in Impostazioni per collegare.
        </div>
      )}
    </div>
  )
}
